// TELOS cascade data — types, verbatim web presets (src/app/telos/cascade-presets.ts)
// and the text resolvers. Pure (no React), so TELOS and ATLAS share one copy.
// Extracted from tools/TelosTool.tsx on 2026-10-12 for the ATLAS consolidation.
import { SECTORS, SECTOR_LABELS, type Sector } from '../domain/olmps';
import { SECTOR_COLOR } from '../v10/theme';

export type Pending = 'hit' | 'miss' | null;

export interface TelosDomainCascade {
  selectedVisionId: string | null;
  customVisionText: string;
  selectedHabitIds: string[];
  customHabits: { id: string; name: string; tasks: string[] }[];
  selectedTaskIds: string[];
  customTaskTexts: string[];
  selectedPrincipleIds?: string[];
  customPrinciples?: string[];
  principleText?: string;
  notes?: string;
}

export interface TelosDomainConfig {
  id: string;
  name: string;
  color: string;
  isActive: boolean;
  visionText: string;
  systemText: string;
  taskText: string;
  chord: [string, string, string];
  cascade: TelosDomainCascade;
}

export interface TelosSettings {
  domains: TelosDomainConfig[];
  lyreEnabled: boolean;
  driftRate: number;
  cycleDuration: '24hr' | '12hr' | 'weekly';
}

export interface TelosDomainState {
  actionOffset: number;
  systemOffset: number;
  streak: number;
  missStreak: number;
  pending: Pending;
}

export function createDefaultCascade(): TelosDomainCascade {
  return {
    selectedVisionId: null,
    customVisionText: '',
    selectedHabitIds: [],
    customHabits: [],
    selectedTaskIds: [],
    customTaskTexts: [],
  };
}

export function createDefaultDomainState(): TelosDomainState {
  return { actionOffset: 0, systemOffset: 0, streak: 0, missStreak: 0, pending: null };
}

// --- Sector mapping for PRESET_ENDEAVOURS lookup ---
export function domainIdToSector(domainId: string): Sector | null {
  return SECTORS.find((sec) => domainId === `telos-${SECTOR_LABELS[sec].toLowerCase()}`) ?? null;
}

// --- cascade-presets.ts, verbatim domain data ---
export interface TelosPresetTask {
  id: string;
  text: string;
}
export interface TelosPresetHabit {
  id: string;
  name: string;
  tasks: TelosPresetTask[];
  principle: string;
}
export interface TelosPresetVision {
  id: string;
  text: string;
}
export interface TelosPresetPrinciple {
  id: string;
  text: string;
  habitId?: string;
}
export interface TelosDomainPreset {
  domainId: string;
  visions: TelosPresetVision[];
  habits: TelosPresetHabit[];
  principles: TelosPresetPrinciple[];
}

export function findPresetForDomain(domain: { id: string; name: string }): TelosDomainPreset | undefined {
  return (
    TELOS_CASCADE_PRESETS.find((p) => p.domainId === domain.id) ||
    TELOS_CASCADE_PRESETS.find((p) => {
      const presetName = p.domainId.replace('telos-', '').toLowerCase();
      return domain.name.toLowerCase() === presetName;
    })
  );
}

export const TELOS_CASCADE_PRESETS: TelosDomainPreset[] = [
  // 1. SOCIAL
  {
    domainId: 'telos-social',
    visions: [
      { id: 'social-v1', text: 'Having relationships that make everyone involved stronger.' },
      { id: 'social-v2', text: 'Having a reputation that precedes and outlasts me.' },
      { id: 'social-v3', text: 'Having a circle rooted in mutual truth and mutual value.' },
      { id: 'social-v4', text: 'Having relationships that survive the truth.' },
      { id: 'social-v5', text: 'Having people who know me and choose to stay.' },
    ],
    habits: [
      {
        id: 'social-a', name: 'I invest in my close relationships',
        principle: 'Proximity is not loyalty.',
        tasks: [
          { id: 'social-a-1', text: 'Reached out to someone I care about' },
          { id: 'social-a-2', text: 'Gave someone my full presence (not just my time)' },
          { id: 'social-a-3', text: 'Did something for someone with no expectation of return' },
        ],
      },
      {
        id: 'social-b', name: 'I communicate with honesty',
        principle: 'The strongest bonds survive honest conflict.',
        tasks: [
          { id: 'social-b-1', text: 'Said something that needed to be said' },
          { id: 'social-b-2', text: 'Asked a real question instead of a polite one' },
          { id: 'social-b-3', text: 'Listened fully before responding' },
        ],
      },
      {
        id: 'social-c', name: 'I manage my reputation intentionally',
        principle: 'Reputation is built in private and revealed in public.',
        tasks: [
          { id: 'social-c-1', text: 'Followed through on something I committed to' },
          { id: 'social-c-2', text: 'Showed up the way I want to be known' },
          { id: 'social-c-3', text: 'Addressed a misunderstanding or unresolved tension' },
        ],
      },
      {
        id: 'social-d', name: 'I expand my network with purpose',
        principle: 'Every relationship is an exchange — the question is whether both sides know the terms.',
        tasks: [
          { id: 'social-d-1', text: 'Connected with someone new who aligns with my direction' },
          { id: 'social-d-2', text: 'Attended or participated in a community or group' },
          { id: 'social-d-3', text: 'Offered value to someone before asking for anything' },
        ],
      },
      {
        id: 'social-e', name: 'I set and maintain boundaries',
        principle: 'You teach people how to treat you by what you tolerate.',
        tasks: [
          { id: 'social-e-1', text: 'Said no to something that didn\'t serve me or the relationship' },
          { id: 'social-e-2', text: 'Enforced a boundary I\'ve already communicated' },
          { id: 'social-e-3', text: 'Removed myself from a draining interaction' },
        ],
      },
    ],
    principles: [
      { id: 'social-p1', text: 'Every relationship is an exchange — the question is whether both sides know the terms.' },
      { id: 'social-p2', text: 'Proximity is not loyalty.' },
      { id: 'social-p3', text: 'You teach people how to treat you by what you tolerate.' },
      { id: 'social-p4', text: 'Reputation is built in private and revealed in public.' },
    ],
  },

  // 2. HEALTH
  {
    domainId: 'telos-health',
    visions: [
      { id: 'health-v1', text: 'Having a body that meets every demand I place on it.' },
      { id: 'health-v2', text: 'Having physical capacity that never limits my life.' },
      { id: 'health-v3', text: 'Having a body that performs when called upon.' },
      { id: 'health-v4', text: 'Having a machine I maintain with the respect it deserves.' },
      { id: 'health-v5', text: 'Having energy that is earned, never borrowed.' },
    ],
    habits: [
      {
        id: 'health-a', name: 'I move consistently',
        principle: 'The body adapts to what you consistently demand of it.',
        tasks: [
          { id: 'health-a-1', text: '30 min intentional movement' },
          { id: 'health-a-2', text: 'Took the harder physical option (stairs, walked, carried)' },
          { id: 'health-a-3', text: 'Stretched or mobilized for 10 min' },
        ],
      },
      {
        id: 'health-b', name: 'I eat with purpose',
        principle: 'You are not what you eat — you are what you consistently eat.',
        tasks: [
          { id: 'health-b-1', text: 'Prepped or cooked at least one meal' },
          { id: 'health-b-2', text: 'Drank water first thing in the morning' },
          { id: 'health-b-3', text: 'Ate mostly whole foods today' },
        ],
      },
      {
        id: 'health-c', name: 'I protect my rest',
        principle: 'Rest is a strategy, not a reward.',
        tasks: [
          { id: 'health-c-1', text: 'In bed by target time' },
          { id: 'health-c-2', text: 'Screens off 30 min before sleep' },
          { id: 'health-c-3', text: 'Got 7+ hours' },
        ],
      },
      {
        id: 'health-d', name: 'I stay on top of my health',
        principle: 'Neglect compounds faster than effort does.',
        tasks: [
          { id: 'health-d-1', text: 'Took vitamins/meds on schedule' },
          { id: 'health-d-2', text: 'Addressed something I\'ve been putting off (appointment, checkup, etc.)' },
          { id: 'health-d-3', text: 'Checked in with how my body feels today' },
        ],
      },
      {
        id: 'health-e', name: 'I keep myself clean and maintained',
        principle: 'How you maintain the outside reflects how you regard the inside.',
        tasks: [
          { id: 'health-e-1', text: 'Full hygiene routine completed' },
          { id: 'health-e-2', text: 'Grooming and appearance handled' },
          { id: 'health-e-3', text: 'Living space is clean and organized' },
        ],
      },
    ],
    principles: [
      { id: 'health-p1', text: 'Energy is earned through discipline, not motivation.' },
      { id: 'health-p2', text: 'The body adapts to what you consistently demand of it.' },
      { id: 'health-p3', text: 'Neglect compounds faster than effort does.' },
      { id: 'health-p4', text: 'Comfort is the enemy of capability.' },
    ],
  },

  // 3. ADMIN
  {
    domainId: 'telos-admin',
    visions: [
      { id: 'admin-v1', text: 'Having a life administered with the same care I\'d give a business.' },
      { id: 'admin-v2', text: 'Having no dropped balls — nothing forgotten, nothing overdue.' },
      { id: 'admin-v3', text: 'Having systems that run themselves once built.' },
      { id: 'admin-v4', text: 'Having a home and paperwork trail that could survive an audit.' },
      { id: 'admin-v5', text: 'Having control over every obligation, not the other way around.' },
    ],
    habits: [
      {
        id: 'admin-a', name: 'I process my inbox and paperwork',
        principle: 'An unopened envelope is a decision deferred, not avoided.',
        tasks: [
          { id: 'admin-a-1', text: 'Cleared email/messages inbox to zero or near-zero' },
          { id: 'admin-a-2', text: 'Opened and processed physical mail or paperwork' },
          { id: 'admin-a-3', text: 'Filed or archived a document that needed it' },
        ],
      },
      {
        id: 'admin-b', name: 'I maintain my calendar and commitments',
        principle: 'A calendar you don\'t trust is worse than no calendar at all.',
        tasks: [
          { id: 'admin-b-1', text: 'Checked tomorrow\'s schedule before bed' },
          { id: 'admin-b-2', text: 'Confirmed or rescheduled an appointment' },
          { id: 'admin-b-3', text: 'Added a forgotten commitment before it became a crisis' },
        ],
      },
      {
        id: 'admin-c', name: 'I keep my systems running',
        principle: 'Systems decay unless someone maintains them.',
        tasks: [
          { id: 'admin-c-1', text: 'Updated a recurring system, checklist, or template' },
          { id: 'admin-c-2', text: 'Automated or simplified a repeated task' },
          { id: 'admin-c-3', text: 'Backed up or organized digital files' },
        ],
      },
      {
        id: 'admin-d', name: 'I stay on top of my obligations',
        principle: 'The obligations you ignore compound into obligations you can\'t ignore.',
        tasks: [
          { id: 'admin-d-1', text: 'Paid or scheduled a bill or renewal before it was late' },
          { id: 'admin-d-2', text: 'Renewed or checked on a license, subscription, or policy' },
          { id: 'admin-d-3', text: 'Responded to a pending request or form' },
        ],
      },
      {
        id: 'admin-e', name: 'I maintain my home and belongings',
        principle: 'A disordered space is administration deferred.',
        tasks: [
          { id: 'admin-e-1', text: 'Did a load of laundry or dishes' },
          { id: 'admin-e-2', text: 'Tidied or reset a space to baseline' },
          { id: 'admin-e-3', text: 'Fixed or scheduled a repair for something broken' },
        ],
      },
    ],
    principles: [
      { id: 'admin-p1', text: 'An unopened envelope is a decision deferred, not avoided.' },
      { id: 'admin-p2', text: 'Systems decay unless someone maintains them.' },
      { id: 'admin-p3', text: 'The obligations you ignore compound into obligations you can\'t ignore.' },
      { id: 'admin-p4', text: 'A calendar you don\'t trust is worse than no calendar at all.' },
    ],
  },

  // 4. PERSONAL
  {
    domainId: 'telos-personal',
    visions: [
      { id: 'personal-v1', text: 'Having a mind that operates with clarity and intention in every situation.' },
      { id: 'personal-v2', text: 'Having a character that requires no explanation.' },
      { id: 'personal-v3', text: 'Having an inner life that drives everything else forward.' },
      { id: 'personal-v4', text: 'Having complete ownership of who I am and who I\'m becoming.' },
      { id: 'personal-v5', text: 'Having a self I built on purpose, not by accident.' },
    ],
    habits: [
      {
        id: 'personal-a', name: 'I learn consistently',
        principle: 'Growth requires honest discomfort, not positive thinking.',
        tasks: [
          { id: 'personal-a-1', text: 'Read or studied for at least 20 min' },
          { id: 'personal-a-2', text: 'Practiced a skill I\'m developing' },
          { id: 'personal-a-3', text: 'Applied something new I recently learned' },
        ],
      },
      {
        id: 'personal-b', name: 'I reflect and journal',
        principle: 'An unexamined pattern will repeat until it\'s named.',
        tasks: [
          { id: 'personal-b-1', text: 'Wrote in my journal today' },
          { id: 'personal-b-2', text: 'Reviewed yesterday\'s decisions honestly' },
          { id: 'personal-b-3', text: 'Identified one pattern I want to change or reinforce' },
        ],
      },
      {
        id: 'personal-c', name: 'I manage my emotional state',
        principle: 'You can only manage what you\'re willing to see clearly.',
        tasks: [
          { id: 'personal-c-1', text: 'Paused before reacting to something that triggered me' },
          { id: 'personal-c-2', text: 'Named an emotion instead of acting on it blindly' },
          { id: 'personal-c-3', text: 'Chose a response instead of defaulting to a reaction' },
        ],
      },
      {
        id: 'personal-d', name: 'I create consistently',
        principle: 'Identity is built by what you repeatedly do, not what you believe about yourself.',
        tasks: [
          { id: 'personal-d-1', text: 'Made something today (wrote, built, designed, composed)' },
          { id: 'personal-d-2', text: 'Worked on my passion project' },
          { id: 'personal-d-3', text: 'Spent time in creative flow' },
        ],
      },
      {
        id: 'personal-e', name: 'I stay spiritually/mentally grounded',
        principle: 'What you avoid defines you more than what you pursue.',
        tasks: [
          { id: 'personal-e-1', text: 'Meditated, prayed, or sat in silence' },
          { id: 'personal-e-2', text: 'Spent time in nature or away from noise' },
          { id: 'personal-e-3', text: 'Practiced gratitude intentionally' },
        ],
      },
    ],
    principles: [
      { id: 'personal-p1', text: 'Identity is built by what you repeatedly do, not what you believe about yourself.' },
      { id: 'personal-p2', text: 'Growth requires honest discomfort, not positive thinking.' },
      { id: 'personal-p3', text: 'What you avoid defines you more than what you pursue.' },
      { id: 'personal-p4', text: 'An unexamined pattern will repeat until it\'s named.' },
    ],
  },

  // 5. ECONOMIC
  {
    domainId: 'telos-economic',
    visions: [
      { id: 'economic-v1', text: 'Having systems that generate freedom and eliminate dependency.' },
      { id: 'economic-v2', text: 'Having a foundation that works independently of my time.' },
      { id: 'economic-v3', text: 'Having infrastructure that compounds whether I\'m present or not.' },
      { id: 'economic-v4', text: 'Having more options every year and fewer dependencies.' },
      { id: 'economic-v5', text: 'Having wealth that reflects the value I create.' },
    ],
    habits: [
      {
        id: 'economic-a', name: 'I manage my money actively',
        principle: 'What you don\'t track, you lose.',
        tasks: [
          { id: 'economic-a-1', text: 'Reviewed my spending today' },
          { id: 'economic-a-2', text: 'Stayed within my daily/weekly budget' },
          { id: 'economic-a-3', text: 'Put something into savings (any amount)' },
        ],
      },
      {
        id: 'economic-b', name: 'I protect what I\'ve built',
        principle: 'Ownership without maintenance is just delayed loss.',
        tasks: [
          { id: 'economic-b-1', text: 'Checked on an account, investment, or policy that matters' },
          { id: 'economic-b-2', text: 'Reviewed a statement or balance for errors' },
          { id: 'economic-b-3', text: 'Addressed a financial loose end' },
        ],
      },
      {
        id: 'economic-c', name: 'I invest in my future',
        principle: 'Systems produce wealth; effort alone produces income.',
        tasks: [
          { id: 'economic-c-1', text: 'Contributed to a long-term goal (retirement, education, asset)' },
          { id: 'economic-c-2', text: 'Learned something about managing money better' },
          { id: 'economic-c-3', text: 'Made a decision today that future me will thank me for' },
        ],
      },
      {
        id: 'economic-d', name: 'I build my career and earning capacity',
        principle: 'Every resource is either growing or shrinking — nothing holds.',
        tasks: [
          { id: 'economic-d-1', text: 'Worked on a project or deliverable that advances my position' },
          { id: 'economic-d-2', text: 'Invested time in a skill with professional return' },
          { id: 'economic-d-3', text: 'Explored a new opportunity or income stream' },
        ],
      },
      {
        id: 'economic-e', name: 'I create and protect income streams',
        principle: 'One income stream is one point of failure.',
        tasks: [
          { id: 'economic-e-1', text: 'Worked on a side project, asset, or income stream outside my main job' },
          { id: 'economic-e-2', text: 'Reviewed or optimized an existing income source' },
          { id: 'economic-e-3', text: 'Took a step toward diversifying how I earn' },
        ],
      },
    ],
    principles: [
      { id: 'economic-p1', text: 'Every resource is either growing or shrinking — nothing holds.' },
      { id: 'economic-p2', text: 'What you don\'t track, you lose.' },
      { id: 'economic-p3', text: 'Systems produce wealth; effort alone produces income.' },
      { id: 'economic-p4', text: 'Ownership without maintenance is just delayed loss.' },
    ],
  },
];

// The five domains derive from the SHAPE sectors (domain/olmps.ts SECTORS /
// SECTOR_LABELS) — ids stay `telos-<name>` so stored settings keep matching.
export const SECTOR_CHORD: Record<Sector, [string, string, string]> = {
  S: ['F4', 'A4', 'C5'],
  H: ['C4', 'E4', 'G4'],
  A: ['D4', 'F#4', 'A4'],
  P: ['G4', 'B4', 'D5'],
  E: ['E4', 'G#4', 'B4'],
};

export function defaultDomainFor(sector: Sector): TelosDomainConfig {
  const base = createDefaultCascade();
  const cascade: TelosDomainCascade =
    sector === 'H'
      ? {
          ...base,
          customHabits: [],
          customTaskTexts: [],
          selectedTaskIds: [],
          customPrinciples: [
            'Order is Life',
            'Order over Action, Always',
            "If you don't know what to do, it's SyQi Order",
          ],
          customVisionText: '',
          selectedHabitIds: ['health-b'],
          selectedVisionId: 'health-v2',
        }
      : base;
  return {
    id: `telos-${SECTOR_LABELS[sector].toLowerCase()}`,
    name: SECTOR_LABELS[sector],
    chord: SECTOR_CHORD[sector],
    color: SECTOR_COLOR[sector],
    cascade,
    isActive: true,
    taskText: '',
    systemText: '',
    visionText: '',
  };
}

// --- settings-context.tsx defaultAppSettings.telosSettings, verbatim ---
export const DEFAULT_TELOS_SETTINGS: TelosSettings = {
  domains: SECTORS.map(defaultDomainFor),
  driftRate: 2,
  lyreEnabled: true,
  cycleDuration: '24hr',
};

// --- cascade-table.tsx text-resolution logic, replicated for read/edit ---
export function resolveVisionText(domain: TelosDomainConfig): string {
  const cascade = domain.cascade;
  if (!cascade) return domain.visionText || '';
  if (cascade.selectedVisionId === '__custom__') return cascade.customVisionText || domain.visionText || '';
  // selectedVisionId now matches a PRESET_ENDEAVOURS string directly (V9 spec §13 #6);
  // older rows (and the seeded Health default) still hold a preset vision id like 'health-v2'.
  const id = cascade.selectedVisionId;
  const legacy = id ? findPresetForDomain(domain)?.visions.find((v) => v.id === id) : undefined;
  return legacy?.text || id || domain.visionText || '';
}

export function resolveHabitNames(domain: TelosDomainConfig): string[] {
  const cascade = domain.cascade;
  const preset = findPresetForDomain(domain);
  const selectedIds = cascade?.selectedHabitIds || [];
  const presetNames = (preset?.habits || []).filter((h) => selectedIds.includes(h.id)).map((h) => h.name);
  const customNames = (cascade?.customHabits || []).map((h) => h.name);
  return [...presetNames, ...customNames];
}

export function availableTasksForDomain(domain: TelosDomainConfig): { id: string; text: string }[] {
  const cascade = domain.cascade;
  const preset = findPresetForDomain(domain);
  const selectedHabitIds = cascade?.selectedHabitIds || [];
  const presetTasks = (preset?.habits || []).filter((h) => selectedHabitIds.includes(h.id)).flatMap((h) => h.tasks);
  const customHabitTasks = (cascade?.customHabits || []).flatMap((h) =>
    h.tasks.map((t, i) => ({ id: `${h.id}-task-${i}`, text: t }))
  );
  return [...presetTasks, ...customHabitTasks];
}

export function resolveActionTexts(domain: TelosDomainConfig): string[] {
  const cascade = domain.cascade;
  const selectedTaskIds = cascade?.selectedTaskIds || [];
  const allTasks = availableTasksForDomain(domain);
  const selected = allTasks.filter((t) => selectedTaskIds.includes(t.id)).map((t) => t.text);
  return [...selected, ...(cascade?.customTaskTexts || [])];
}

export function availablePrinciplesForDomain(domain: TelosDomainConfig): { id: string; text: string }[] {
  const cascade = domain.cascade;
  const preset = findPresetForDomain(domain);
  const selectedHabitIds = cascade?.selectedHabitIds || [];
  const habitPrinciples = (preset?.habits || [])
    .filter((h) => selectedHabitIds.includes(h.id))
    .map((h) => ({ id: `${h.id}-principle`, text: h.principle }));
  const generalPrinciples = preset?.principles || [];
  return [...habitPrinciples, ...generalPrinciples];
}

export function resolvePrincipleTexts(domain: TelosDomainConfig): string[] {
  const cascade = domain.cascade;
  const selectedPrincipleIds = cascade?.selectedPrincipleIds || [];
  const allPrinciples = availablePrinciplesForDomain(domain);
  const selected = allPrinciples.filter((p) => selectedPrincipleIds.includes(p.id)).map((p) => p.text);
  return [...selected, ...(cascade?.customPrinciples || [])];
}
